import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, AppBar, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import AdminGlobalSettings from '../../components/Admin/AdminGlobalSettings';
import AdminUserManagement from '../../components/AdminUserManagement';
import AdminExistingQuestions from '../../components/Admin/AdminExistingQuestions';
import FirmManagementPage from '../../components/Admin/FirmManagementPage';
import stripeConfig from '../../config/stripe';
import { functions } from '../../firebase';
import { toast } from 'react-toastify';
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const AdminDashboard = () => {
    const { user, logout } = useAuth();
    const [currentTab, setCurrentTab] = useState(0);
    const [clients, setClients] = useState([]);

    // Fetch all clients for the admin view
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsSnapshot = await getDocs(collection(db, 'clients'));
                const clientsList = clientsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setClients(clientsList);
                console.log('[ADMIN] Loaded clients:', clientsList.length);
            } catch (error) {
                console.error('[ADMIN] Error fetching clients:', error);
            }
        };

        fetchClients();
    }, []);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f4f6f8' }}>
            <AppBar position="static" color="default" elevation={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'white' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                        SavvaiEdge Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1">{user?.email}</Typography>
                        <Button variant="contained" color="primary" onClick={logout}>
                            Logout
                        </Button>
                    </Box>
                </Box>
            </AppBar>
            <Box sx={{ flexGrow: 1, p: 3 }}>
                <Paper elevation={2}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={currentTab} onChange={handleTabChange} aria-label="admin portal tabs">
                            <Tab label="Dashboard" id="admin-tab-0" />
                            <Tab label="User & Client Management" id="admin-tab-1" />
                            <Tab label="Firm Portal" id="admin-tab-2" />
                            <Tab label="Global Settings" id="admin-tab-3" />
                        </Tabs>
                    </Box>
                    <TabPanel value={currentTab} index={0}>
                        <AdminExistingQuestions 
                            clientId="ALL" 
                            user={user}
                            isAdmin={true} 
                            clients={clients}
                            clientIsActive={true}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={1}>
                        <AdminUserManagement />
                    </TabPanel>
                    <TabPanel value={currentTab} index={2}>
                        <FirmManagementPage />
                    </TabPanel>
                    <TabPanel value={currentTab} index={3}>
                        <AdminGlobalSettings />
                    </TabPanel>

                    {/* Admin Claims Sync - Add this near the Stripe test section */}
<Paper sx={{ p: 3, mt: 3, border: '2px dashed orange' }}>
    <Typography variant="h6" color="warning.main" gutterBottom>
        🔧 Sync Super-Admin Claims (One-Time Fix)
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
        This will set Firebase Auth custom claims for all users with admin: true in Firestore.
        Required for Firestore security rules to work correctly on list queries.
    </Typography>
    <Button
        variant="contained"
        color="warning"
        onClick={async () => {
            try {
                const { httpsCallable } = await import('firebase/functions');
                const syncAdminClaims = httpsCallable(functions, 'adminSyncAllAdminClaims');
                const result = await syncAdminClaims({});
                console.log('Sync result:', result.data);
                toast.success(result.data.message);
            } catch (err) {
                console.error('Sync failed:', err);
                toast.error(`Failed: ${err.message}`);
            }
        }}
    >
        SYNC ADMIN CLAIMS
    </Button>
</Paper>

                     {/* TEMPORARY: Stripe Test Section - Remove after testing */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed orange', borderRadius: 1, backgroundColor: '#fff3e0' }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            🧪 Stripe Integration Test (Remove After Testing)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            This will create a Stripe checkout session with 14-day trial for the currently logged-in user.
                        </Typography>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={async () => {
                                try {
                                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                                    const functions = getFunctions();
                                    const createCheckout = httpsCallable(functions, 'createCheckoutSession');
                                    
                                    console.log('🧪 Creating checkout session...');
                                    const result = await createCheckout({ 
    idempotencyKey: `test-${Date.now()}` 
});
console.log('✅ Checkout session created:', result.data.sessionId);
alert('Checkout session created! Redirecting to Stripe...');

// Load Stripe and redirect properly
const { loadStripe } = await import('@stripe/stripe-js');
const stripe = await loadStripe(stripeConfig.publishableKey);
const { error } = await stripe.redirectToCheckout({ sessionId: result.data.sessionId });
if (error) {
    alert(`Redirect error: ${error.message}`);
}
                                    
                                } catch (error) {
                                    console.error('❌ Error:', error);
                                    alert(`Error creating checkout: ${error.message}`);
                                }
                            }}
                        >
                            🧪 Test Stripe Checkout (14-Day Trial)
                        </Button>
                    </Box>

                    {/* TEMPORARY: Theme Canonicalization Backfill - Remove after running */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed #4caf50', borderRadius: 1, backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h6" color="success.main" gutterBottom>
                            🔧 Theme Canonicalization Backfill (One-Time)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Populates canonicalThemes on historical documents for accurate Industry Insights aggregation.
                            Run Dry Run first to preview, then Run Backfill to apply changes.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={async () => {
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillCanonicalThemes');
                                        
                                        console.log('🔍 Running dry run...');
                                        const result = await backfillFn({ 
                                            dryRun: true, 
                                            batchSize: 500, 
                                            collection: 'all' 
                                        });
                                        console.log('✅ Dry run complete:', result.data);
                                        alert(`DRY RUN COMPLETE:\n\nQuestions: ${result.data.results.questions.updated} would be updated\nSignals: ${result.data.results.signals.updated} would be updated\nMemos: ${result.data.results.decisionMemos.updated} would be updated\n\nTotal: ${result.data.results.totalUpdated} documents\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Dry run error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Dry Run (Preview)
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={async () => {
                                    if (!window.confirm('This will update historical documents with canonicalThemes. Continue?')) {
                                        return;
                                    }
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillCanonicalThemes');
                                        
                                        console.log('🚀 Running backfill...');
                                        const result = await backfillFn({ 
                                            dryRun: false, 
                                            batchSize: 500, 
                                            collection: 'all' 
                                        });
                                        console.log('✅ Backfill complete:', result.data);
                                        alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.results.questions.updated}\nSignals updated: ${result.data.results.signals.updated}\nMemos updated: ${result.data.results.decisionMemos.updated}\n\nTotal: ${result.data.results.totalUpdated} documents updated\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Backfill error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🚀 Run Backfill
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Note: If you have more than 500 documents, run multiple times until "0 updated" is reported.
                        </Typography>
                    </Box>

                    {/* TEMPORARY: AI Governance Backfill - Remove after running in all environments */}
                    <Box sx={{ p: 3, mt: 2, border: '2px dashed #1976d2', borderRadius: 1, backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            🔒 AI Governance Backfill (One-Time)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Adds <code>provider</code> and <code>aiAuditMetadata</code> fields to historical Q&A, SavvaiSignals, and Decision Memos for governance compliance.
                            Run Dry Run first to preview, then Run Backfill to apply changes.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={async () => {
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillProviderField');
                                        
                                        console.log('🔍 Running AI Governance dry run...');
                                        const result = await backfillFn({ dryRun: true, batchSize: 500 });
                                        console.log('✅ Dry run complete:', result.data);
                                        alert(`DRY RUN COMPLETE:\n\nQuestions: ${result.data.results.questions.updated} would be updated (${result.data.results.questions.skipped} already compliant)\nSavvaiSignals: ${result.data.results.businessUpdates.updated} would be updated (${result.data.results.businessUpdates.skipped} already compliant)\nDecision Memos: ${result.data.results.decisionMemos.updated} would be updated (${result.data.results.decisionMemos.skipped} already compliant)\n\nTotal: ${result.data.totalUpdated} documents\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Dry run error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🔍 Dry Run (Preview)
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    if (!window.confirm('This will add provider and aiAuditMetadata to historical documents. Continue?')) {
                                        return;
                                    }
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const backfillFn = httpsCallable(functions, 'backfillProviderField');
                                        
                                        console.log('🚀 Running AI Governance backfill...');
                                        const result = await backfillFn({ dryRun: false, batchSize: 500 });
                                        console.log('✅ Backfill complete:', result.data);
                                        alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.results.questions.updated}\nSavvaiSignals updated: ${result.data.results.businessUpdates.updated}\nDecision Memos updated: ${result.data.results.decisionMemos.updated}\n\nTotal: ${result.data.totalUpdated} documents updated\n\nCheck console for full details.`);
                                        
                                    } catch (error) {
                                        console.error('❌ Backfill error:', error);
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                🚀 Run Backfill
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Note: Run in each environment (Dev → CC → Prod). If you have more than 500 documents, run multiple times until "0 updated" is reported.
                        </Typography>
                    </Box>
                    {/* TEMPORARY: Anonymization Metadata Backfill - Remove after running in all environments */}
<Box sx={{ p: 3, mt: 2, border: '2px dashed #9c27b0', borderRadius: 1, backgroundColor: '#f3e5f5' }}>
    <Typography variant="h6" color="secondary" gutterBottom>
        🛡️ Anonymization Metadata Backfill (Phase 2)
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
        Adds <code>anonymizationApplied</code>, <code>anonymizationTimestamp</code>, and <code>anonymizationDetails</code> 
        to historical Q&A, SavvaiSignals, and Decision Memos for SOC 2 data protection compliance.
        Documents created before Phase 1 deployment will be marked with <code>backfilled: true</code>.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
                if (!window.confirm('This will add anonymization metadata to all historical documents missing it. Continue?')) {
                    return;
                }
                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const backfillFn = httpsCallable(functions, 'backfillAnonymizationMetadata');
                    
                    console.log('🛡️ Running Anonymization Metadata backfill...');
                    const result = await backfillFn({});
                    console.log('✅ Backfill complete:', result.data);
                    alert(`BACKFILL COMPLETE:\n\nQuestions updated: ${result.data.totals.questionsUpdated} (${result.data.totals.questionsSkipped} already had metadata)\nSignals updated: ${result.data.totals.signalsUpdated} (${result.data.totals.signalsSkipped} already had metadata)\nDecision Memos updated: ${result.data.totals.memosUpdated} (${result.data.totals.memosSkipped} already had metadata)\n\nTotal: ${result.data.totals.totalUpdated} documents updated\n\n${result.data.errors ? 'Errors: ' + result.data.errors.length : 'No errors'}\n\nCheck console for full details.`);
                    
                } catch (error) {
                    console.error('❌ Backfill error:', error);
                    alert(`Error: ${error.message}`);
                }
            }}
        >
            🛡️ Run Anonymization Backfill
        </Button>
    </Box>
    <Typography variant="caption" color="text.secondary">
        Note: Run in each environment (Dev → Prod). If you have more than 500 documents per collection, run multiple times until "0 updated" is reported. Safe to run multiple times - already-updated documents are skipped.
    </Typography>
</Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default AdminDashboard;